import React, { useState, useEffect } from 'react';
import './CustomerProfile.css';
import { getCustomerById, updateCustomer } from '../../services/customersService';
import { getCurrentUser } from '../../services/authService';

const CustomerProfile = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    mobileNumber: '',
    address: '',
    companyName: '',
    gstNumber: '',
    panNumber: ''
  });

  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [customerId, setCustomerId] = useState(null);
  const [originalData, setOriginalData] = useState(null);

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return 'CU';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // Map API customer data to form data
  const mapApiToForm = (customerData) => {
    // Handle different response formats
    const customer = customerData?.data || customerData || {};
    
    // Build address string from address fields
    const addressParts = [];
    if (customer.address_line1) addressParts.push(customer.address_line1);
    if (customer.address_line2) addressParts.push(customer.address_line2);
    if (customer.city) addressParts.push(customer.city);
    if (customer.state) addressParts.push(customer.state);
    if (customer.pincode) addressParts.push(customer.pincode);
    const address = addressParts.length > 0 ? addressParts.join(', ') : '';

    return {
      fullName: customer.customer_name || customer.customerName || '',
      email: customer.email_id || customer.emailId || customer.email || '',
      mobileNumber: customer.mobile_number || customer.mobileNumber || '',
      address: address,
      companyName: customer.company_name || customer.companyName || '',
      gstNumber: customer.gst_number || customer.gstNumber || '',
      panNumber: customer.pan_number || customer.panNumber || ''
    };
  };

  // Map form data to API format
  const mapFormToApi = (formData) => {
    // For simplicity, we'll save the entire address to address_line1
    // In a production app, you might want to parse it more intelligently
    // to split into address_line1, address_line2, city, state, pincode
    
    return {
      customer_name: formData.fullName,
      email_id: formData.email,
      mobile_number: formData.mobileNumber,
      address_line1: formData.address || null, // Save full address to address_line1
      pan_number: formData.panNumber || null,
      // Note: company_name and gst_number are not in the customers table schema
      // If your backend supports them, uncomment these:
      // company_name: formData.companyName || null,
      // gst_number: formData.gstNumber || null,
    };
  };

  // Fetch customer data on mount
  useEffect(() => {
    const fetchCustomerData = async () => {
      try {
        setIsLoading(true);
        const currentUser = getCurrentUser();
        
        if (!currentUser) {
          throw new Error('User not logged in');
        }

        // Get customer_id from user data
        const id = currentUser.customer_id || currentUser.customerId;
        
        if (!id) {
          throw new Error('Customer ID not found in user data');
        }

        setCustomerId(id);
        
        // Fetch customer data from API
        const response = await getCustomerById(id);
        const mappedData = mapApiToForm(response);
        
        setFormData(mappedData);
        setOriginalData(mappedData);
      } catch (error) {
        console.error('Error fetching customer data:', error);
        setErrorMessage(error.message || 'Failed to load customer profile');
        setShowError(true);
        setTimeout(() => setShowError(false), 5000);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCustomerData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!customerId) {
      setErrorMessage('Customer ID not found');
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    try {
      setIsSaving(true);
      setShowError(false);
      
      // Map form data to API format
      const apiData = mapFormToApi(formData);
      
      // Update customer via API
      await updateCustomer(customerId, apiData);
      
      // Update original data
      setOriginalData({ ...formData });
    setIsEditing(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
      } catch (error) {
      console.error('Error updating customer profile:', error);
      setErrorMessage(error.message || 'Failed to update profile. Please try again.');
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form data to original
    if (originalData) {
      setFormData({ ...originalData });
    }
    setIsEditing(false);
    setShowError(false);
  };

  if (isLoading) {
    return (
      <div className="customer-profile-container">
        <div className="profile-header">
          <h1 className="profile-title">My Profile</h1>
        </div>
        <div className="profile-content">
          <div className="profile-card" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-profile-container">
      <div className="profile-header">
        {!isEditing && (
          <button className="edit-profile-btn" onClick={() => setIsEditing(true)}>
            ✏️ Edit Profile
          </button>
        )}
      </div>

      {showSuccess && (
        <div className="success-message">
          Profile updated successfully!
        </div>
      )}

      {showError && (
        <div className="error-message" style={{ 
          backgroundColor: '#fee', 
          color: '#c33', 
          padding: '12px 16px', 
          borderRadius: '4px', 
          marginBottom: '16px',
          border: '1px solid #fcc'
        }}>
          {errorMessage}
        </div>
      )}

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-avatar-section">
            <div className="profile-avatar">{getInitials(formData.fullName)}</div>
            <h2 className="profile-name">{formData.fullName || 'Customer'}</h2>
            <p className="profile-email">{formData.email || 'No email'}</p>
          </div>

          <div className="profile-form-section">
            <h3 className="section-title">Personal Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input
                  type="text"
                  name="fullName"
                  className="form-input"
                  value={formData.fullName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  name="email"
                  className="form-input"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Mobile Number</label>
                <input
                  type="text"
                  name="mobileNumber"
                  className="form-input"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group full-width">
                <label className="form-label">Address</label>
                <textarea
                  name="address"
                  className="form-input"
                  rows="3"
                  value={formData.address}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>
            </div>

            <h3 className="section-title" style={{ marginTop: '32px' }}>Company Information</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Company Name</label>
                <input
                  type="text"
                  name="companyName"
                  className="form-input"
                  value={formData.companyName}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label className="form-label">GST Number</label>
                <input
                  type="text"
                  name="gstNumber"
                  className="form-input"
                  value={formData.gstNumber}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>

              <div className="form-group">
                <label className="form-label">PAN Number</label>
                <input
                  type="text"
                  name="panNumber"
                  className="form-input"
                  value={formData.panNumber}
                  onChange={handleChange}
                  disabled={!isEditing}
                />
              </div>
            </div>

            {isEditing && (
              <div className="form-actions">
                <button 
                  className="btn-cancel" 
                  onClick={handleCancel}
                  disabled={isSaving}
                >
                  Cancel
                </button>
                <button 
                  className="btn-save" 
                  onClick={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerProfile;

